import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '../../../../components'
import { useMessage, useTooltip } from '../../../../hooks'
import { clientReviewService } from '../../services'
import { appName } from '../../appConfig'
import { PERSPECTIVES } from '../../helpers/perspectives'
import '../../index.css'

function RichText({ text, className }) {
  if (!text) return null
  const lines = text.split('\n')
  const elements = []
  let listItems = []

  const flushList = (key) => {
    if (listItems.length) {
      elements.push(
        <ul key={key} className="cc-trend-list">
          {listItems.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      )
      listItems = []
    }
  }

  lines.forEach((line, i) => {
    const bullet = line.match(/^[-*]\s+(.*)/)
    if (bullet) {
      listItems.push(bullet[1])
    } else {
      flushList(`list-${i}`)
      if (line.trim() === '') {
        elements.push(<div key={i} className="cc-trend-spacer" />)
      } else {
        elements.push(<div key={i}>{line}</div>)
      }
    }
  })
  flushList('list-end')

  return <div className={className}>{elements}</div>
}

function scoreColor(score) {
  if (score === 4) return '#3a9a6a'
  if (score === 3) return '#7A5FC0'
  if (score === 2) return '#C05F7A'
  if (score === 1) return '#d04040'
  return null
}

function ScoreDot({ score, color, size = 26 }) {
  const c = color || scoreColor(score)
  if (!score || !c) return <span className="cc-trend-score-dot-empty">—</span>
  return (
    <span className="cc-trend-score-dot" style={{ width: size, height: size, background: c + '22', color: c }}>{score}</span>
  )
}

function Delta({ curr, prev }) {
  if (!curr || !prev || curr === prev) return null
  const up = curr > prev
  return (
    <span className={`cc-trend-delta ${up ? 'up' : 'down'}`}>
      {up ? '↑' : '↓'}{Math.abs(curr - prev)}
    </span>
  )
}

// One small sparkline panel per perspective instead of a single shared-axis chart.
// At 10+ reviews, 4 lines mostly collapse onto the same 2-3 score levels and become
// unreadable where they overlap; separate panels can never cross each other, so
// each perspective's shape stays legible regardless of how much the others converge.
// The panel scales with .cc-trend-mini-grid's one-row-of-4 columns (which fill the
// page) — no explicit height on the <svg>, so its 240:78 aspect ratio grows the
// chart's actual height along with its width instead of just stretching it thin.
function TrendChart({ reviews, onPointClick }) {
  const { showTooltip, hideTooltip } = useTooltip()

  if (reviews.length < 2) return null
  const W = 240, H = 78, PAD_L = 16, PAD_R = 6, PAD_T = 6, PAD_B = 16
  const innerW = W - PAD_L - PAD_R
  const innerH = H - PAD_T - PAD_B
  const n = reviews.length
  const xOf = i => PAD_L + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW)
  const yOf = s => PAD_T + innerH - ((s - 1) / 3) * innerH

  // "Q1 2024" -> quarter code every tick, year appended only where it changes
  // (first tick, or the tick after a year rollover) so the axis doesn't repeat
  // "2024" ten times.
  const xLabels = reviews.map((r, i) => {
    const [q, year] = (r.client_review_period || '').split(' ')
    const prevYear = i > 0 ? (reviews[i - 1].client_review_period || '').split(' ')[1] : null
    return year && year !== prevYear ? `${q} '${year.slice(2)}` : q
  })

  return (
    <div className="cc-trend-mini-grid">
      {PERSPECTIVES.map(p => {
        const scores = reviews.map(r => r.client_review_data?.perspectives?.[p.key]?.score)
        const pts = scores.map((s, i) => s != null ? { x: xOf(i), y: yOf(s) } : null)
        const pairs = []
        for (let i = 0; i < pts.length - 1; i++) {
          if (pts[i] && pts[i + 1]) pairs.push([pts[i], pts[i + 1]])
        }
        const lastIdx = pts.reduce((acc, pt, i) => pt ? i : acc, -1)
        const lastScore = lastIdx >= 0 ? scores[lastIdx] : null

        return (
          <div className="cc-trend-mini-card" key={p.key}>
            <div className="cc-trend-mini-head">
              <span className="cc-trend-mini-dot" style={{ background: p.color }} />
              <span className="cc-trend-mini-label">{p.labelSv}</span>
              {lastScore != null && <span className="cc-trend-mini-score" style={{ color: p.color }}>{lastScore}</span>}
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="cc-trend-mini-svg">
              {[1, 2, 3, 4].map(s => (
                <g key={s}>
                  <line x1={PAD_L} y1={yOf(s)} x2={W - PAD_R} y2={yOf(s)} className="cc-trend-mini-grid-line" />
                  <text x={PAD_L - 3} y={yOf(s) + 2} textAnchor="end" className="cc-trend-mini-axis-label">{s}</text>
                </g>
              ))}
              {pairs.map(([a, b], li) => (
                <line key={li} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={p.color} strokeWidth="2" strokeLinecap="round" />
              ))}
              {pts.map((pt, i) => pt && (
                <circle key={i} cx={pt.x} cy={pt.y} r={i === lastIdx ? 3 : 2} fill={p.color} stroke="#fff" strokeWidth={i === lastIdx ? 1.5 : 1} />
              ))}
              {/* Invisible, oversized hit targets — the visible dots above are too small
                  to hover/click precisely, especially once ~10 of them share a 240-unit-wide
                  viewBox. Hover shows the shared app tooltip; click scrolls to that review. */}
              {pts.map((pt, i) => pt && (
                <circle
                  key={`hit-${i}`} cx={pt.x} cy={pt.y} r="8" fill="transparent" className="cc-trend-mini-hit"
                  style={{ cursor: onPointClick ? 'pointer' : 'default' }}
                  onMouseEnter={() => showTooltip({ title: reviews[i].client_review_period, bodyText: `${p.labelSv}: ${scores[i]}` })}
                  onMouseLeave={hideTooltip}
                  onClick={() => onPointClick && onPointClick(reviews[i].client_review_slug)}
                />
              ))}
              {xLabels.map((label, i) => (
                <text key={i} x={xOf(i)} y={H - 3} textAnchor="middle" className="cc-trend-mini-axis-label">{label}</text>
              ))}
            </svg>
          </div>
        )
      })}
    </div>
  )
}

// Extracted from the old standalone ClientReviewTrend page (client_cases app) so
// it can be embedded as a tab inside ClientDetail. Owns its own data fetch —
// ClientDetail only needs to pass the client's slug.
export const ReviewTrendSection = ({ clientSlug }) => {
  const navigate = useNavigate()
  const { showMessage } = useMessage()

  const [reviews, setReviews]     = useState([])
  const [loading, setLoading]     = useState(true)
  const [openCards, setOpenCards] = useState(new Set())
  const [openRecruitment, setOpenRecruitment] = useState(new Set())

  useEffect(() => {
    const ctrl = new AbortController()
    clientReviewService.getByClient(clientSlug, ctrl.signal)
      .then(res => {
        const sorted = res.clientReviews || []
        setReviews(sorted)
        if (sorted.length > 0) {
          setOpenCards(new Set([sorted[sorted.length - 1].client_review_slug]))
        }
        setLoading(false)
      })
      .catch(err => {
        if (err.name !== 'AbortError') { showMessage('Kunde inte ladda utvärderingar', 'error'); setLoading(false) }
      })
    return () => ctrl.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSlug])

  const toggleCard = (reviewSlug) => {
    setOpenCards(prev => {
      const next = new Set(prev)
      next.has(reviewSlug) ? next.delete(reviewSlug) : next.add(reviewSlug)
      return next
    })
  }

  const toggleRecruitment = (reviewSlug) => {
    setOpenRecruitment(prev => {
      const next = new Set(prev)
      next.has(reviewSlug) ? next.delete(reviewSlug) : next.add(reviewSlug)
      return next
    })
  }

  const scrollToReview = (reviewSlug) => {
    const el = document.getElementById(`cc-review-${reviewSlug}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (loading) return <p style={{ color: 'var(--color-dark-grey)' }}>Laddar…</p>

  const reversed = reviews.slice().reverse()

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button value="+ Ny utvärdering" className="button main-button" onClick={() => navigate(`/${appName}/${clientSlug}/reviews/new`)} />
      </div>

      {reviews.length === 0 ? (
        <p style={{ color: 'var(--color-dark-grey)', fontStyle: 'italic' }}>
          Inga utvärderingar ännu. Skapa den första för att börja följa trender.
        </p>
      ) : (
        <>
          {reviews.length >= 2 && (
            <div className="cc-trend-card">
              <div className="cc-trend-card-title">Poängtrend</div>
              <TrendChart reviews={reviews} onPointClick={scrollToReview} />
            </div>
          )}

          {reversed.map((r, ri) => {
            const prev = ri < reversed.length - 1 ? reversed[ri + 1] : null
            const isLatest = ri === 0
            const isOpen = openCards.has(r.client_review_slug)
            const isRecruitmentOpen = openRecruitment.has(r.client_review_slug)
            const team = r.client_review_data?.team || []
            const candidates = r.client_review_data?.recruitment_candidates || []
            const why = r.client_review_data?.why_this_client
            const hasWhy = why?.kpi_aspects || why?.outside_kpi_aspects
            const date = r.client_review_data?.date
            const formattedDate = date
              ? new Date(date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'long', year: 'numeric' })
              : null

            return (
              <div key={r.client_review_slug} id={`cc-review-${r.client_review_slug}`} className={`cc-trend-review-card${isLatest ? ' latest' : ''}`}>
                {/* Card header */}
                <div className="cc-trend-review-header">
                  <span className="cc-trend-review-period">{r.client_review_period}</span>
                  {isLatest && <span className="cc-trend-latest-badge">Senaste</span>}
                  <Button value="Redigera" className="button cc-trend-edit-btn" onClick={() => navigate(`/${appName}/${clientSlug}/reviews/${r.client_review_slug}/edit`)} />
                  {formattedDate && <span className="cc-trend-review-date">{formattedDate}</span>}
                </div>

                {/* Score pills */}
                <div className="cc-trend-scores-row">
                  {PERSPECTIVES.map(p => {
                    const score = r.client_review_data?.perspectives?.[p.key]?.score
                    const prevScore = prev?.client_review_data?.perspectives?.[p.key]?.score
                    return (
                      <div key={p.key} className="cc-trend-score-item">
                        <ScoreDot score={score} color={p.color} />
                        <span className="cc-trend-score-label">{p.labelSv}</span>
                        <Delta curr={score} prev={prevScore} />
                      </div>
                    )
                  })}
                </div>

                {/* Team chips */}
                {team.length > 0 && (
                  <div className="cc-trend-team-row">
                    <span className="cc-trend-team-label">Team</span>
                    {team.map(name => (
                      <span key={name} className="cc-tag">{name}</span>
                    ))}
                  </div>
                )}

                {/* Summary */}
                {r.client_review_data?.summary && (
                  <div className="cc-trend-summary">
                    <RichText text={r.client_review_data.summary} className="cc-trend-richtext" />
                  </div>
                )}

                {/* Why this client — part of the main frame, always visible (not tucked behind a toggle) */}
                {hasWhy && (
                  <div className="cc-trend-subsection">
                    <div className="cc-trend-subsection-title">Varför denna kund</div>
                    {why.kpi_aspects && <RichText text={why.kpi_aspects} className="cc-trend-richtext" />}
                    {why.outside_kpi_aspects && <div className="cc-trend-why-outside"><RichText text={why.outside_kpi_aspects} className="cc-trend-richtext muted" /></div>}
                  </div>
                )}

                {/* Recruitment candidates — its own drop-down, independent of the perspectives panel */}
                {candidates.length > 0 && (
                  <>
                    <div className="cc-trend-expand-toggle" onClick={() => toggleRecruitment(r.client_review_slug)}>
                      <span className="cc-trend-expand-label">Rekryteringskandidater ({candidates.length})</span>
                      <span className={`cc-trend-expand-chevron${isRecruitmentOpen ? ' open' : ''}`}>▼</span>
                    </div>
                    {isRecruitmentOpen && (
                      <div className="cc-trend-subsection">
                        <div className="cc-table-scroll">
                          <table className="cc-table">
                            <thead><tr><th>Namn</th><th>Roll</th><th>Nästa steg</th><th>Ansvarig</th></tr></thead>
                            <tbody>
                              {candidates.map((c, i) => (
                                <tr key={i}>
                                  <td>{c.person_uuid ? <Link className="link" to={`/network/persons/${c.person_uuid}`}>{c.person_name}</Link> : (c.person_name || c.name)}</td>
                                  <td style={{ color: 'var(--color-dark-grey)' }}>{c.role}</td>
                                  <td style={{ color: 'var(--color-dark-grey)' }}>{c.next_step}</td>
                                  <td style={{ color: 'var(--color-dark-grey)' }}>{c.responsible}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Expand toggle */}
                <div className="cc-trend-expand-toggle" onClick={() => toggleCard(r.client_review_slug)}>
                  <span className="cc-trend-expand-label">Perspektiv &amp; individuella poäng</span>
                  <span className={`cc-trend-expand-chevron${isOpen ? ' open' : ''}`}>▼</span>
                </div>

                {/* Perspective detail panel */}
                {isOpen && (
                  <div>
                    {PERSPECTIVES.map((p, pi) => {
                      const perspData = r.client_review_data?.perspectives?.[p.key]
                      const hasContent = perspData?.current_state || perspData?.goal || perspData?.activities || (perspData?.individual || []).length > 0 || team.length > 0
                      if (!hasContent) return null
                      return (
                        <div key={p.key} className={`cc-trend-persp-detail${pi < PERSPECTIVES.length - 1 ? ' bordered' : ''}`} style={{ borderLeftColor: p.color }}>
                          {/* Perspective heading */}
                          <div className="cc-trend-persp-heading">
                            <ScoreDot score={perspData?.score} color={p.color} size={26} />
                            <span className="cc-trend-persp-title" style={{ color: p.color }}>{p.labelSv}</span>
                          </div>

                          {/* Group text fields */}
                          {(perspData?.current_state || perspData?.goal || perspData?.activities) && (
                            <div className="cc-trend-persp-grid">
                              {[
                                { key: 'current_state', label: 'Nuläge' },
                                { key: 'goal',          label: 'Mål' },
                                { key: 'activities',    label: 'Aktiviteter' },
                              ].map(({ key, label }) => perspData?.[key] ? (
                                <div key={key}>
                                  <div className="cc-trend-persp-field-label">{label}</div>
                                  <RichText text={perspData[key]} className="cc-trend-richtext small" />
                                </div>
                              ) : null)}
                            </div>
                          )}

                          {/* Individual scores */}
                          {team.length > 0 && (
                            <div className="cc-trend-individual-box">
                              <div className="cc-trend-individual-heading">Individuellt</div>
                              {team.map(name => {
                                const ind = (perspData?.individual || []).find(e => e.name === name) || {}
                                return (
                                  <div key={name} className="cc-trend-individual-row">
                                    <span className="cc-trend-individual-name">{name}</span>
                                    <ScoreDot score={ind.score} size={26} />
                                    {ind.comment
                                      ? <span className="cc-trend-individual-comment">{ind.comment}</span>
                                      : <span className="cc-trend-individual-empty">—</span>
                                    }
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
